import axios from '@/api/axios';
import { getDateRange, getPreviousDayDate } from './dateUtils';

/**
 * Fetch orders for current day
 * @param {Function} getOrders - Vuex action to fetch orders
 * @param {string} catalogId - Catalog ID
 * @param {string} now - Current timestamp
 * @returns {Promise} Promise resolving to orders data
 */
export function fetchCurrentOrders(getOrders, catalogId, now) {
  return getOrders({
    params: {
      query: {
        ...getDateRange(now, 0),
        catalogId,
      },
    },
  });
}

/**
 * Fetch orders for previous day (Friday for weekends)
 * @param {Function} getOrders - Vuex action to fetch orders
 * @param {string} catalogId - Catalog ID
 * @param {string} now - Current timestamp
 * @returns {Promise} Promise resolving to orders data
 */
export function fetchPreviousDayOrders(getOrders, catalogId, now) {
  const previousDayDate = getPreviousDayDate(now);
  return getOrders({
    params: {
      query: {
        from: previousDayDate.format('YYYY-MM-DD'),
        to: previousDayDate.clone().add(1, 'day').format('YYYY-MM-DD'),
        catalogId,
      },
    },
  });
}

/**
 * Fetch orders from last week (same day)
 * @param {Function} getOrders - Vuex action to fetch orders
 * @param {string} catalogId - Catalog ID
 * @param {string} now - Current timestamp
 * @returns {Promise} Promise resolving to orders data
 */
export function fetchLastWeekOrders(getOrders, catalogId, now) {
  return getOrders({
    params: {
      query: {
        ...getDateRange(now, -7),
        catalogId,
      },
    },
  });
}

/**
 * Fetch all three periods of orders in parallel
 * @param {Function} getOrders - Vuex action to fetch orders
 * @param {string} catalogId - Catalog ID
 * @param {string} now - Current timestamp
 * @returns {Promise} Promise resolving to { current, previousDay, lastWeek }
 */
export async function fetchAllOrders(getOrders, catalogId, now) {
  try {
    const [currentRes, previousDayRes, lastWeekRes] = await Promise.all([
      fetchCurrentOrders(getOrders, catalogId, now),
      fetchPreviousDayOrders(getOrders, catalogId, now),
      fetchLastWeekOrders(getOrders, catalogId, now),
    ]);

    return {
      current: currentRes.data,
      previousDay: previousDayRes.data,
      lastWeek: lastWeekRes.data,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching orders:', error);
    throw error;
  }
}

/**
 * Fetch catalog meals and ingredients
 * @param {string} catalogId - Catalog ID
 * @returns {Promise} Promise resolving to { meals, ingredients }
 */
export async function fetchCatalogMenuData(catalogId) {
  try {
    const [mealsRes, ingredientsRes] = await Promise.all([
      axios.get(`shop/${catalogId}/meals`),
      axios.get(`shop/${catalogId}/meals/ingredients`),
    ]);

    return {
      meals: mealsRes.data.meals,
      ingredients: ingredientsRes.data.ingredients,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching menu data:', error);
    throw error;
  }
}
